import React, { useEffect, useState } from 'react';
import { ProviderEvidence, DocumentViewer, ProviderDocumentUrl, Spinner, Card } from '@enter/core';
import { inject, observer } from 'mobx-react';
import { IInjectedRootStore } from 'stores';
import styled from '@emotion/styled';

const FullWidthCardWrapper = styled.div`
    width: 100%;
    
    > div {
        width: 100%;
        display: block !important;
    }
`;

const SectionContainer = styled.div`
    width: 100%;
    display: flex;
    flex-direction: column;
    gap: 24px;
`;

const HeaderSection = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding-bottom: 16px;
`;

const HeaderTitle = styled.h2`
    margin: 0;
    font-size: 20px;
    font-weight: 600;
    color: #374151;
`;

const Content = styled.div`
    display: flex;
    flex-direction: column;
    gap: 24px;
`;

const FieldGroup = styled.div`
    display: flex;
    flex-direction: column;
    gap: 16px;
`;

const Field = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
`;

const FieldLabel = styled.label`
    font-weight: 600;
    font-size: 14px;
    color: #374151;
`;

const FieldValue = styled.div`
    font-size: 14px;
    color: #6B7280;
    padding: 8px 12px;
    background-color: #F9FAFB;
    border: 1px solid #E5E7EB;
    border-radius: 6px;
    min-height: 20px;
`;

const DocumentSection = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
`;

const DocumentViewerContainer = styled.div`
    border: 1px solid #E5E7EB;
    border-radius: 6px;
    min-height: 200px;
    background-color: #F9FAFB;
    display: flex;
    align-items: center;
    justify-content: center;
`;

const DocumentPlaceholder = styled.div`
    text-align: center;
    color: #6B7280;
    font-size: 14px;
    
    small {
        color: #9CA3AF;
        font-size: 12px;
    }
`;

interface EvidenceViewSectionProps {
    evidence: ProviderEvidence;
}

type IInjectedProps = EvidenceViewSectionProps & IInjectedRootStore;

export const EvidenceViewSection = inject('rootStore')(
    observer((props: EvidenceViewSectionProps) => {
        const { evidence, rootStore } = props as IInjectedProps;
        const [documentLoading, setDocumentLoading] = useState(false);
        const [documentUrl, setDocumentUrl] = useState<ProviderDocumentUrl | undefined>();

        const formatDate = (dateString: string | null | undefined): string => {
            if (!dateString) return '-';
            return new Date(dateString).toLocaleDateString();
        };

        // Load document when component mounts
        useEffect(() => {
            if (!evidence.documentId || !rootStore) {
                return;
            }

            const loadDocument = async () => {
                setDocumentLoading(true);
                try {
                    const url = await rootStore.claimStore.loadDocumentDownloadUrl(evidence.documentId!);
                    console.log('url', url);
                    setDocumentUrl(url);
                } catch (error) {
                    console.error('Error loading document:', error);
                } finally {
                    setDocumentLoading(false);
                }
            };

            loadDocument();
        }, [evidence.documentId, rootStore]);

    const isDenied = evidence.status === 'DENIED';
    const isApproved = evidence.status === 'APPROVED';

    return (
        <FullWidthCardWrapper>
            <Card size="large">
                <SectionContainer>
                    <HeaderSection>
                        <HeaderTitle>{evidence.evidenceType?.name || 'Evidence Details'}</HeaderTitle>
                    </HeaderSection>

                    <Content>
                        <FieldGroup>                            
                            <Field>
                                <FieldLabel>Description</FieldLabel>
                                <FieldValue>{evidence.evidenceType?.description || '-'}</FieldValue>
                            </Field>
                            
                            {isDenied && evidence.denyReason && (
                                <Field>
                                    <FieldLabel>Deny Reason</FieldLabel>
                                    <FieldValue>{evidence.denyReason}</FieldValue>
                                </Field>
                            )}
                            
                            {isDenied && evidence.denied && (
                                <Field>
                                    <FieldLabel>Deny Date</FieldLabel>
                                    <FieldValue>{formatDate(evidence.denied)}</FieldValue>
                                </Field>
                            )}
                            
                            {isApproved && evidence.approved && (
                                <Field>
                                    <FieldLabel>Approved Date</FieldLabel>
                                    <FieldValue>{formatDate(evidence.approved)}</FieldValue>
                                </Field>
                            )}
                            
                            <Field>
                                <FieldLabel>Notes</FieldLabel>
                                <FieldValue>{evidence.notes || '-'}</FieldValue>
                            </Field>
                        </FieldGroup>
                        
                        {evidence.documentId && (
                            <DocumentSection>
                                <FieldLabel>Document</FieldLabel>
                                <DocumentViewerContainer>
                                    {documentLoading ? (
                                        <DocumentPlaceholder>
                                            <Spinner />
                                            <br />
                                            <small>Loading document...</small>
                                        </DocumentPlaceholder>
                                    ) : documentUrl?.url ? (
                                        <DocumentViewer url={documentUrl.url} />
                                    ) : (
                                        <DocumentPlaceholder>
                                            Document ID: {evidence.documentId}
                                            <br />
                                            <small>Unable to load document</small>
                                        </DocumentPlaceholder>
                                    )}
                                </DocumentViewerContainer>
                            </DocumentSection>
                        )}
                    </Content>
                </SectionContainer>
            </Card>
        </FullWidthCardWrapper>
    );
    }),
);

